import React, { useEffect, useMemo, useState } from "react";
import Title from "../../components/Title";
import { useDispatch, useSelector } from "react-redux";
import axios from "../../MyAxios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  ADD_COMMUNITY_CONNECTION,
  CREATE_POST,
  ROOT,
} from "../../constants/api";
import Layout from "../../Layout";
import { resetPost, setSelectedTags } from "../../redux/postSlice";
import useLoggedIn from "../../hooks/useLoggedIn";

import { addToPosts } from "@redux/prevPostsSlice";
import { Autocomplete, TextField, Typography } from "@mui/material";
import clsx from "clsx";
import { useTheme } from "@emotion/react";
import Loader from "@components/Loader";
import { parseAlphabets } from "@helpers";

function Tag({ name, onClick, isSelected }) {
  return (
    <div className="flex">
      <div
        onClick={onClick}
        style={{
          backgroundColor: isSelected
            ? "rgba(81, 205, 160, 0.25)"
            : "rgb(98, 98, 98, 0.1)",
        }}
        className="px-2 py-2 rounded-md cursor-pointer"
      >
        <Typography className="select-none">{name}</Typography>
      </div>
    </div>
  );
}
function SelectTags({ open = false }) {
  useLoggedIn();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const theme = useTheme();
  const post = useSelector((state) => state.post);
  const [search, setSearch] = useState("");
  const tagKeys = useMemo(() => Object.keys(tags), [tags]);
  const {
    tags: prevTags,
    selectedPostCommunities,
    isGroupOnlyPost,
    selectedGroups,
    selectedTags,
    title,
    description,
  } = useSelector((state) => state.post);

  const fetchData = () => {
    setLoading(true);
    axios
      .get("tags", { params: { communityIds: selectedPostCommunities, search } })
      .then((res) => {
        setTags(res.data);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if(prevTags?.length)
      dispatch(setSelectedTags([...selectedTags, ...prevTags.map((tag) => tag.id)]));
  }, [id, prevTags]);

  useEffect(() => {
    if(selectedPostCommunities?.length) {
      fetchData();
      dispatch(setSelectedTags([]));
    }
  }, [selectedPostCommunities]);

  const onTagClick = (tagId) => {
    if(selectedTags.length >= 5 && !selectedTags.includes(tagId))
      return toast.error('You can select 5 tags at most');

    const updatedTags = selectedTags.includes(tagId)
      ? selectedTags.filter((t) => t !== tagId)
      : [...selectedTags, tagId];

    dispatch(setSelectedTags(updatedTags));
  };

  const handleTagsChange = (newTags) => {
    dispatch(setSelectedTags(newTags));
  };

  const onSearchClick = () => {
    fetchData();
  };

  const handlePost = async () => {
    // if (selectedTags.length === 0) {
    //   return toast.error("Please select at least one tag");
    // }

    if (id) {
      try {
        const res = await axios.post(`/post/edit/${id}`, {
          description: post.description,
          mediaId: post.media,
          connections: Array.from(post.connections),
          communities: Array.from(post.communities),
          groups: Array.from(post.selectedGroups),
          tags: selectedTags,
          ageRestricted: post.ageRestricted,
        });
        dispatch(resetPost());
        dispatch(addToPosts(res.data));
        navigate(ROOT);
      } catch (error) {}
    } else {
      try {
        await axios
          .post(CREATE_POST, {
            description: post.description,
            mediaId: post.media,
            connections: Array.from(post.connections),
            communities: Array.from(post.communities),
            groups: Array.from(post.selectedGroups),
            selectedPostCommunities: post.selectedPostCommunities,
            tags: selectedTags,
            ageRestricted: post.ageRestricted,
          })
          .then((res) => {
            toast.success("Post created successfully");
            dispatch(resetPost());
            dispatch(addToPosts(res.data));
            navigate(ROOT, { replace: true });
          });
      } catch (error) {}
    }
  };

  useEffect(() => {
    if(!open)
      return;

    if(tagKeys && tags && selectedTags && title && description) {
      let tagsList = tagKeys.map(key => tags[key]).flat();

      let postWords = title?.split(' ')?.filter(word => word.length > 3);

      if(description)
        postWords = postWords.concat(description.split(' ')?.filter(word => word?.length > 3));

      let autoSelectedCats = {};
      postWords?.forEach(word => {
        let wordSmallcase = parseAlphabets(word?.toLowerCase());

        if(!wordSmallcase)
          return;

        tagsList.forEach(tag => {
          let tagSmallcase = tag.subcategory?.toLowerCase();

          if(!tagSmallcase)
            return;

          if(
            !autoSelectedCats[tag.id]
            && (tagSmallcase?.includes(wordSmallcase) || wordSmallcase?.includes(tagSmallcase))
          )
            autoSelectedCats[tag.id] = tag;
        })
      });

      let newTags = Object.values(autoSelectedCats).filter(tag => !selectedTags.some(selectedTag => selectedTag.id === tag.id));

      dispatch(setSelectedTags([
        ...selectedTags,
        ...newTags
      ]));
    }
  }, [open, title, description, tags, tagKeys]);

  if (loading)
    return <Loader />;

  let tagsToShow = [];

  tagKeys.forEach((key) => {
    tags[key].forEach(tag => {
      tagsToShow.push(tag);
    })
  });

  return (
    <Autocomplete
      size='small'
      className="mx-5 mb-4 mt-4"
      multiple
      clearIcon={null}
      disableCloseOnSelect
      options={tagsToShow}
      getOptionLabel={(option) => option?.subcategory}
      value={selectedTags}
      onChange={(e, selectedTags) => handleTagsChange(selectedTags)}
      ListboxProps={{ style: { overscrollBehavior: 'none' } }}
      componentsProps={{
        popper: {
          modifiers: [
            {
              name: 'flip',
              enabled: false
            },
            {
               name: 'preventOverflow',
               enabled: false
             }
          ]
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Topics"
          variant="outlined"
          placeholder="Search topics"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px'
            }
          }}
        />
      )}
    />
  )

  return (
    <div className="m-5">
      <TextField
        sx={{ "& fieldset": { border: "none" } }}
        className="pb-4 sticky top-[108px] bg-white"
        type="text"
        size="small"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyUp={(e) => e.key === "Enter" && onSearchClick()}
        placeholder="Search Topics"
        InputProps={{
          disableUnderline: true,
          sx: {
            borderRadius: 2,
            padding: 0.2,
            backgroundColor: "rgb(98, 98, 98, 0.1)",
            border: "none",
          },
        }}
      />
      <div>
        {/* {tagKeys.map((key) => (
          <div className="mb-2">
            <Typography
              sx={{ fontSize: "18px" }}
              className="font-semibold pb-2"
            >
              {key}
            </Typography>
            <div className="flex gap-2 flex-wrap">
              {tags[key].map((tag) => (
                <Tag
                  onClick={() => onTagClick(tag.id)}
                  name={tag.subcategory}
                  isSelected={selectedTags.includes(tag.id)}
                />
              ))}
            </div>
          </div>
        ))} */}
        <div className="flex gap-2 flex-wrap">
          {tagKeys?.length === 0 && (
            <Typography>
              No topics for selected community
            </Typography>
          )}
          {tagKeys.map((key) => (
            tags[key].map((tag) => (
              <Tag
                onClick={() => onTagClick(tag.id)}
                name={tag.subcategory}
                isSelected={selectedTags?.includes(tag.id)}
              />
            ))
          ))}
        </div>
      </div>
    </div>
  );
}

export default SelectTags;
